import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, BookOpen, Check, X, XCircle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, setAuthToken } from '../utils/auth';
import api from '../utils/api';
import './Header.css';
import logo from '../images/I-Pro Logo 097abf.png';
import { API_BASE_URL } from '../utils/apiBase';

const formatTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const DISMISSED_NOTIFICATIONS_KEY = 'dismissedNotificationIds';

const getDismissedNotificationIds = () => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_NOTIFICATIONS_KEY) || '[]');
  } catch (error) {
    return [];
  }
};

const saveDismissedNotificationIds = (ids) => {
  localStorage.setItem(DISMISSED_NOTIFICATIONS_KEY, JSON.stringify(ids));
};

const Header = () => {
  const [greetingInfo, setGreetingInfo] = useState({ greeting: '', icon: '' });
  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      let greeting = '';
      let icon = '';
      if (hour >= 5 && hour < 12) {
        greeting = 'Good Morning';
        icon = '🌅';
      } else if (hour >= 12 && hour < 17) {
        greeting = 'Good Afternoon';
        icon = '☀️';
      } else if (hour >= 17 && hour < 21) {
        greeting = 'Good Evening';
        icon = '🌇';
      } else {
        greeting = 'Good Night';
        icon = '🌙';
      }
      setGreetingInfo({ greeting, icon });
    };
    updateGreeting();
    const interval = setInterval(updateGreeting, 60000);
    return () => clearInterval(interval);
  }, []);

  const navigate = useNavigate();
  const name = localStorage.getItem('name');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [activeRequest, setActiveRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const panelRef = useRef(null);
  const hasLoadedNotificationsRef = useRef(false);
  const previousUnreadIdsRef = useRef(new Set());
  const audioReadyRef = useRef(false);

  const playNotificationSound = useCallback(() => {
    if (!audioReadyRef.current || typeof window === 'undefined') return;

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    try {
      const audioContext = new AudioContextClass();
      const createBellOscillator = (type, frequency, gainValue, startAt, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = type;
        oscillator.frequency.setValueAtTime(frequency, startAt);
        oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.72, startAt + duration);

        gainNode.gain.setValueAtTime(0.0001, startAt);
        gainNode.gain.exponentialRampToValueAtTime(gainValue, startAt + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.start(startAt);
        oscillator.stop(startAt + duration + 0.02);
      };

      const startAt = audioContext.currentTime;
      createBellOscillator('triangle', 1318, 0.18, startAt, 0.42);
      createBellOscillator('sine', 1760, 0.08, startAt + 0.03, 0.34);

      const secondBellAt = startAt + 0.46;
      createBellOscillator('triangle', 1174, 0.16, secondBellAt, 0.4);
      createBellOscillator('sine', 1567, 0.075, secondBellAt + 0.03, 0.32);

      window.setTimeout(() => {
        audioContext.close().catch(() => {});
      }, 1300);
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated()) return;

    setLoadingNotifications(true);
    try {
      const res = await api.get(`${API_BASE_URL}/api/notifications`);
      const dismissedIds = new Set(getDismissedNotificationIds());
      const visibleNotifications = (res.data?.notifications || []).filter(
        (item) => !dismissedIds.has(item._id)
      );
      const unreadNotifications = visibleNotifications.filter((item) => !item.isRead);
      const unreadIds = new Set(unreadNotifications.map((item) => item._id));
      const hasNewUnread = unreadNotifications.some(
        (item) => !previousUnreadIdsRef.current.has(item._id)
      );

      if (hasLoadedNotificationsRef.current && hasNewUnread) {
        playNotificationSound();
      }

      previousUnreadIdsRef.current = unreadIds;
      hasLoadedNotificationsRef.current = true;
      setNotifications(visibleNotifications);
      setUnreadCount(res.data?.unreadCount || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  }, [playNotificationSound]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const unlockAudio = () => {
      audioReadyRef.current = true;
    };

    window.addEventListener('pointerdown', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  const handleLogout = () => {
    setAuthToken(null);
    localStorage.clear();
    navigate('/login');
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`${API_BASE_URL}/api/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((item) =>
          item._id === notificationId ? { ...item, isRead: true } : item
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const clearNotification = async (notificationId) => {
    const nextIds = [...new Set([...getDismissedNotificationIds(), notificationId])];
    saveDismissedNotificationIds(nextIds);
    setNotifications((prev) => prev.filter((item) => item._id !== notificationId));
  };

  const openNotification = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }

    if (
      notification.type === 'branch_request_submitted' &&
      notification.data?.requestId
    ) {
      try {
        const res = await api.get(
          `${API_BASE_URL}/api/insurer-branch-requests/${notification.data.requestId}`
        );
        setActiveRequest(res.data);
        setShowRejectBox(false);
        setRejectionReason('');
        setActionError('');
      } catch (error) {
        console.error('Failed to load branch request details:', error);
      }
    }
  };

  const closeRequestModal = () => {
    setActiveRequest(null);
    setShowRejectBox(false);
    setRejectionReason('');
    setActionError('');
  };

  const submitRequestAction = async (action) => {
    if (!activeRequest?._id) return;

    if (action === 'reject' && !rejectionReason.trim()) {
      setActionError('Rejection reason is required.');
      return;
    }

    setActionLoading(true);
    setActionError('');
    try {
      if (action === 'approve') {
        await api.post(
          `${API_BASE_URL}/api/insurer-branch-requests/${activeRequest._id}/approve`
        );
      } else {
        await api.post(
          `${API_BASE_URL}/api/insurer-branch-requests/${activeRequest._id}/reject`,
          { rejectionReason: rejectionReason.trim() }
        );
      }

      await loadNotifications();
      closeRequestModal();
    } catch (error) {
      setActionError(
        error?.response?.data?.error || 'Failed to update branch request.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <header className="main-header">
        <div className="header-left">
          <div className="header-logo">
            <img
              src={logo}
              alt="Logo"
              style={{ width: '160px', height: '40px', marginRight: '16px' }}
            />
          </div>
        </div>

        <div className="header-right">
          <div className="header-notification-wrap" ref={panelRef}>
            <button
              type="button"
              className="notification-toggle-btn"
              onClick={() => setShowNotifications((current) => !current)}
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="notification-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="notification-panel">
                <div className="notification-panel-head">
                  <h3>Notifications</h3>
                  <div className="notification-panel-actions">
                    <button
                      type="button"
                      className="notification-history-btn"
                      onClick={() => {
                        setShowNotifications(false);
                        navigate('/notifications');
                      }}
                      title="Open notification history"
                    >
                      <BookOpen size={16} />
                    </button>
                    <button type="button" onClick={loadNotifications}>
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="notification-list">
                  {loadingNotifications ? (
                    <div className="notification-empty">Loading notifications...</div>
                  ) : notifications.length === 0 ? (
                    <div className="notification-empty">No notifications found.</div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification._id}
                        className={`notification-item ${notification.isRead ? '' : 'unread'}`}
                      >
                        <button
                          type="button"
                          className="notification-item-main"
                          onClick={() => openNotification(notification)}
                        >
                          <div className="notification-item-top">
                            <strong>{notification.title}</strong>
                            <span>{formatTime(notification.createdAt)}</span>
                          </div>
                          <p>{notification.message}</p>
                          {notification.data?.status && (
                            <small className={`notification-status ${notification.data.status}`}>
                              {notification.data.status}
                            </small>
                          )}
                        </button>
                        <button
                          type="button"
                          className="notification-clear-icon-btn"
                          onClick={() => clearNotification(notification._id)}
                          title="Clear notification"
                        >
                          <span className="notification-clear-glyph">×</span>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <span className="greeting">{greetingInfo.icon} {greetingInfo.greeting}, {name || 'Guest'}</span>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </header>

      {activeRequest && createPortal(
        <div className="notification-modal-overlay">
          <div className="notification-modal">
            <div className="notification-modal-head">
              <div>
                <h3>Branch Approval Request</h3>
                <p>Review branch details before approving or rejecting.</p>
              </div>
              <button
                type="button"
                className="notification-close-btn"
                onClick={closeRequestModal}
              >
                <X size={18} />
              </button>
            </div>

            <div className="notification-request-grid">
              <div>
                <span>Insurer</span>
                <strong>{activeRequest.insurerName}</strong>
              </div>
              <div>
                <span>Branch</span>
                <strong>{activeRequest.branchName}</strong>
              </div>
              <div>
                <span>Requested By</span>
                <strong>{activeRequest.requestedBy?.userName || '-'}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong className={`notification-status ${activeRequest.status}`}>
                  {activeRequest.status}
                </strong>
              </div>
              <div>
                <span>Contact Person</span>
                <strong>{activeRequest.contactPersonName}</strong>
              </div>
              <div>
                <span>Contact Number</span>
                <strong>{activeRequest.contactNumber}</strong>
              </div>
              <div>
                <span>Email</span>
                <strong>{activeRequest.email}</strong>
              </div>
              <div>
                <span>Location</span>
                <strong>{[activeRequest.city, activeRequest.state].filter(Boolean).join(', ')}</strong>
              </div>
              <div className="full-width">
                <span>Address</span>
                <strong>{activeRequest.address}</strong>
              </div>
              {activeRequest.auditLogs?.length > 0 && (
                <div className="full-width notification-audit">
                  <span>Audit Log</span>
                  {activeRequest.auditLogs.map((item, index) => (
                    <p key={`${item.action}-${index}`}>
                      {item.action} by {item.actedByName || 'System'} on {formatTime(item.at)}
                      {item.note ? `: ${item.note}` : ''}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {showRejectBox && (
              <div className="notification-reject-box">
                <label>Reason for rejection</label>
                <textarea
                  rows="3"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
              </div>
            )}

            {actionError && <p className="notification-error">{actionError}</p>}

            <div className="notification-modal-actions">
              {activeRequest.status === 'pending' ? (
                <>
                  <button
                    type="button"
                    className="approve-btn"
                    disabled={actionLoading}
                    onClick={() => submitRequestAction('approve')}
                  >
                    <Check size={16} /> Approve
                  </button>
                  {!showRejectBox ? (
                    <button
                      type="button"
                      className="reject-btn"
                      disabled={actionLoading}
                      onClick={() => {
                        setShowRejectBox(true);
                        setActionError('');
                      }}
                    >
                      <XCircle size={16} /> Reject
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="reject-btn"
                      disabled={actionLoading}
                      onClick={() => submitRequestAction('reject')}
                    >
                      <XCircle size={16} /> Confirm Rejection
                    </button>
                  )}
                </>
              ) : (
                <button type="button" className="close-secondary-btn" onClick={closeRequestModal}>
                  Close
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Header;
